/* ==========================================================================
   Cascadia Overland Co. - Apex story section

   GOES IN: assets/apex-story.js
   PAIRED WITH: sections/apex-story.liquid, assets/apex-story.css

   Loaded with defer, so the DOM is already parsed when this runs. It
   also re-runs on shopify:section:load, which is what fires when you
   drag the section around in the theme editor. The init guard on the
   root element stops it from binding twice.
   ========================================================================== */

(function () {
  'use strict';

  function initApexStory() {
    var root = document.getElementById('co-apex-coapx');
    if (!root) return;
    if (root.dataset.coInit) return;
    root.dataset.coInit = '1';

    /* ----------------------------------------------------------------------
       1. Viewport width minus scrollbar. Feeds the full-bleed breakout in
       the CSS; clientWidth avoids a Windows horizontal scrollbar.
       ---------------------------------------------------------------------- */

    function setVW() {
      document.documentElement.style.setProperty(
        '--co-vw', document.documentElement.clientWidth + 'px'
      );
    }
    setVW();
    window.addEventListener('resize', setVW);
    window.addEventListener('orientationchange', setVW);

    /* ----------------------------------------------------------------------
       2. Scroll to Configure Your Camper. Matches the heading text, falls
       back to the cart form. Edit CONFIG_TEXT if the heading is renamed.
       ---------------------------------------------------------------------- */

    var CONFIG_TEXT = 'configure your camper';

    function findConfigureTarget() {
      var heads = document.querySelectorAll('h1,h2,h3,h4,h5,h6,legend');
      for (var i = 0; i < heads.length; i++) {
        if (root.contains(heads[i])) continue;
        if (heads[i].textContent.trim().toLowerCase().indexOf(CONFIG_TEXT) === 0) {
          return heads[i];
        }
      }
      return document.querySelector('form[action*="/cart/add"]')
        || document.querySelector('#MainProduct, .product__info-wrapper, product-info');
    }

    function stickyOffset() {
      var pad = 16;
      var candidates = document.querySelectorAll('header, .header, #shopify-section-header, sticky-header');
      for (var i = 0; i < candidates.length; i++) {
        var pos = window.getComputedStyle(candidates[i]).position;
        if (pos === 'fixed' || pos === 'sticky') {
          return candidates[i].getBoundingClientRect().height + pad;
        }
      }
      return pad;
    }

    root.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-co-configure]');
      if (!trigger) return;
      var target = findConfigureTarget();
      if (!target) return;
      e.preventDefault();
      var y = target.getBoundingClientRect().top + window.pageYOffset - stickyOffset();
      window.scrollTo({
        top: y,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    });

    /* ----------------------------------------------------------------------
       3. YouTube facades. Runs for every .co-yt in the section, so the
       walkthrough playlist and the installation video both work. A button
       carrying data-yt-playlist opens into the playlist; without one it
       plays the single video. Nothing loads from YouTube until a click.
       ---------------------------------------------------------------------- */

    var ytBtns = root.querySelectorAll('.co-yt');

    Array.prototype.forEach.call(ytBtns, function (ytBtn) {
      var ytVideo = ytBtn.getAttribute('data-yt-video');
      var ytList  = ytBtn.getAttribute('data-yt-playlist');
      var ytTitle = ytBtn.getAttribute('aria-label') || 'Cascadia Overland video';
      if (!ytVideo) return;

      /* Thumbnail fallback: maxresdefault only exists for HD uploads,
         hqdefault always does. The naturalWidth check is required
         because the error event may have already fired. */
      var thumb = ytBtn.querySelector('img');
      if (thumb) {
        var swapThumb = function () {
          if (thumb.dataset.coFallback) return;
          thumb.dataset.coFallback = '1';
          thumb.src = 'https://i.ytimg.com/vi/' + ytVideo + '/hqdefault.jpg';
        };
        thumb.addEventListener('error', swapThumb);
        if (thumb.complete && thumb.naturalWidth === 0) swapThumb();
      }

      /* Preconnect on hover so playback starts faster */
      var warmed = false;
      function warm() {
        if (warmed) return;
        warmed = true;
        ['https://www.youtube-nocookie.com', 'https://www.google.com'].forEach(function (href) {
          var l = document.createElement('link');
          l.rel = 'preconnect';
          l.href = href;
          document.head.appendChild(l);
        });
      }
      ytBtn.addEventListener('mouseenter', warm, { once: true });
      ytBtn.addEventListener('focus', warm, { once: true });

      ytBtn.addEventListener('click', function () {
        if (ytBtn.classList.contains('is-live')) return;

        var src = 'https://www.youtube-nocookie.com/embed/' + ytVideo + '?';
        if (ytList) src += 'list=' + ytList + '&';
        src += 'autoplay=1&rel=0&modestbranding=1&playsinline=1';

        var frame = document.createElement('iframe');
        frame.src = src;
        frame.title = ytTitle;
        frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        frame.setAttribute('allowfullscreen', '');
        frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

        ytBtn.innerHTML = '';
        ytBtn.appendChild(frame);
        ytBtn.classList.add('is-live');
        ytBtn.style.cursor = 'default';
        ytBtn.removeAttribute('aria-label');
      });
    });

    /* ----------------------------------------------------------------------
       4. 360 interior viewer. Pannellum loads on demand as the section
       nears the viewport, so it costs nothing on first paint. Panoramas
       must be equirectangular (2:1). Add, remove, or reorder views in
       PANO_VIEWS below.
       ---------------------------------------------------------------------- */

    var PANO_VIEWS = [
      { label: 'Slide-out', url: 'https://cdn.shopify.com/s/files/1/0716/0981/0998/files/apex_360_dinette_closed.jpg?v=1783653856' },
      { label: 'Bed',       url: 'https://cdn.shopify.com/s/files/1/0716/0981/0998/files/apex-pano-bed-out.jpg' },
      { label: 'Couch',     url: 'https://cdn.shopify.com/s/files/1/0716/0981/0998/files/apex-pano-couch.jpg' },
      { label: 'Dinette',   url: 'https://cdn.shopify.com/s/files/1/0716/0981/0998/files/apex_360_dinette_out.jpg?v=1783653821' }
    ].filter(function (v) { return v.url && v.url.trim().length; });

    var panoEl = root.querySelector('#co-apex-pano');
    var panoCol = panoEl ? panoEl.closest('.co-split-media') : null;

    if (panoEl && !PANO_VIEWS.length) {
      if (panoCol) panoCol.style.display = 'none';
    } else if (panoEl) {
      var PANO_V = '2.5.6';
      var panoBooted = false;

      var panoFail = function () {
        panoEl.innerHTML = '<div class="co-pano-load">360 view unavailable</div>';
      };

      var initPano = function () {
        if (!window.pannellum) { panoFail(); return; }
        var scenes = {};
        PANO_VIEWS.forEach(function (v, i) {
          scenes['s' + i] = { type: 'equirectangular', panorama: v.url, autoLoad: true };
        });
        panoEl.innerHTML = '';
        var viewer = pannellum.viewer('co-apex-pano', {
          default: { firstScene: 's0', autoLoad: true, showZoomCtrl: true, mouseZoom: true, hfov: 100, sceneFadeDuration: 600 },
          scenes: scenes
        });

        if (PANO_VIEWS.length < 2) return;
        var nav = root.querySelector('#co-apex-pano-nav');
        PANO_VIEWS.forEach(function (v, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'co-pano-btn' + (i === 0 ? ' is-active' : '');
          b.textContent = v.label;
          b.addEventListener('click', function () {
            viewer.loadScene('s' + i);
            nav.querySelectorAll('.co-pano-btn').forEach(function (x) { x.classList.remove('is-active'); });
            b.classList.add('is-active');
          });
          nav.appendChild(b);
        });
      };

      var bootPano = function () {
        if (panoBooted) return;
        panoBooted = true;
        var css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://cdn.jsdelivr.net/npm/pannellum@' + PANO_V + '/build/pannellum.css';
        document.head.appendChild(css);
        var js = document.createElement('script');
        js.src = 'https://cdn.jsdelivr.net/npm/pannellum@' + PANO_V + '/build/pannellum.js';
        js.onload = initPano;
        js.onerror = panoFail;
        document.head.appendChild(js);
      };

      if ('IntersectionObserver' in window) {
        var pio = new IntersectionObserver(function (en) {
          if (en[0].isIntersecting) { pio.disconnect(); bootPano(); }
        }, { rootMargin: '500px 0px' });
        pio.observe(panoEl);
      } else {
        bootPano();
      }
    }

    /* ----------------------------------------------------------------------
       5. Scroll reveal
       ---------------------------------------------------------------------- */

    var items = root.querySelectorAll('[data-reveal]');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach(function (el) { io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initApexStory);
  } else {
    initApexStory();
  }

  /* Theme editor: re-init after a section is re-rendered. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('#co-apex-coapx')) initApexStory();
  });
})();
